// Import necessary dependencies and components from React and external libraries
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    convertToRaw,
    EditorState,
    RawDraftContentState,
    getDefaultKeyBinding,
    ContentState,
    convertFromRaw,
} from "draft-js";
import _Editor, { PluginEditorProps } from "@draft-js-plugins/editor";
import createMentionPlugin, { Popover } from "@draft-js-plugins/mention";
import Mention from "./MentionComponent";
import "draft-js/dist/Draft.css";
import "@draft-js-plugins/mention/lib/plugin.css";
import Loader from "components/common/Loader";
import useGetProductList from "hooks/inventoryHooks/useGetProductList";
import Button from "components/common/Button";
import SynthTooltip from "components/common/SynthTooltip";
import { useLocation } from "react-router-dom";
import { useMergedSearchParams } from "helpers/mergeSearchParams";

// Typecast the Editor to use React functional component with PluginEditorProps
const Editor = _Editor as unknown as React.FC<React.PropsWithChildren<PluginEditorProps>>;

interface CustomEditorProps {
    message: string;
    setMessage: React.Dispatch<React.SetStateAction<string>>;
    setSelectedIds: React.Dispatch<React.SetStateAction<{ [key: string]: any }[]>>;
    selectedIds: { [key: string]: any }[];
    currentSelectedIds: { [key: string]: any }[];
    setCurrentSelectedIds: React.Dispatch<React.SetStateAction<{ [key: string]: any }[]>>;
    sendMessage: (message: string) => void;
    hasPreviousMessage: boolean;
    threadId: string;
}

// Custom editor component
const CustomEditor: React.FC<CustomEditorProps> = ({
    message,
    setMessage,
    setSelectedIds,
    selectedIds,
    sendMessage,
    hasPreviousMessage,
    threadId,
}) => {
    const location = useLocation();
    const { updateSearchParams } = useMergedSearchParams();

    const { selectedProducts } = location.state || [];
    const [activeTab, setActiveTab] = useState<string>("Finished Goods");
    const [editorState, setEditorState] = useState<EditorState>(EditorState.createEmpty());
    const [open, setOpen] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const tabs = [
        {
            label: "Finished goods",
            value: "Finished Goods",
        },
        {
            label: "Components",
            value: "Raw Material",
        },
        {
            label: "Work in Progress (WIP)",
            value: "Work-In-Progress",
        },
    ];
    const ref = useRef<_Editor>(null);

    const productRef = useRef<HTMLDivElement>(null);

    const {
        data,
        isFetching: productListLoading,
        pagination,
        setProductCategory,
        search,
        handleSearch,
        handleDebouncedChange,
        updatedPage,
        setUpdatedPage,
        setDebouncedSearch,
    } = useGetProductList(productRef.current, activeTab || "Finished Goods", true, true);

    useEffect(() => {
        data.length > 0
            ? setSuggestions(
                  data?.map((item) => ({
                      ...item,
                      name: `SKU:${item?.sku}-(${item?.productName})`,
                  }))
              )
            : setSuggestions([]);
    }, [data]);

    const onOpenChange = useCallback((newOpen) => {
        setOpen(newOpen);
    }, []);

    // Callback to handle filtering of mentions based on user input
    const onSearchChange = useCallback(({ value }: { value: string }) => {
        setDebouncedSearch(value);
    }, []);

    // Memoize the mention plugin to optimize performance
    const { MentionSuggestions, plugins } = useMemo(() => {
        const mentionPlugin = createMentionPlugin({
            entityMutability: "IMMUTABLE",
            theme: {
                mention: "text-[#0054C2]",
            },
            popperOptions: {
                modifiers: [
                    {
                        name: "offset",
                        options: { offset: [0, 8] },
                    },
                ],
            },
            mentionPrefix: "@",
            supportWhitespace: true,
        });
        const { MentionSuggestions } = mentionPlugin;
        const plugins = [mentionPlugin];
        return { plugins, MentionSuggestions };
    }, []);

    // Function to extract mentions from the editor's content
    const getMentions = useCallback((contentState: RawDraftContentState) => {
        const entityMap = contentState.entityMap;
        const users = [];
        for (const key in entityMap) {
            if (entityMap[key].data?.mention) {
                users.push(entityMap[key].data.mention);
            }
        }
        return users;
    }, []);

    // Function to get the current content of the editor and log it
    const handleAddProduct = () => {
        const rawState = convertToRaw(editorState.getCurrentContent());
        const mentions = getMentions(rawState);
        const uniqueMentions = Array.from(new Set(mentions)); // Remove duplicates
        const newIds = uniqueMentions.filter(
            (item) => !selectedIds.some((sel) => sel._id === item._id)
        );
        setSelectedIds((prev) => [...prev, ...newIds]);
    };

    const rawContent = (mentions, message) => {
        const mentionStrings = mentions.map((m) => `@SKU:${m.sku}-(${m.productName})`);
        const mentionText = mentionStrings.join(" ");
        const msg = message
            ? message
            : "Create a sales target forecast for the following product(s):";
        const fullText = `${msg} ${mentionText}`;

        const entityRanges = [];
        const entityMap = {};

        let runningOffset = fullText.indexOf("@SKU:");

        mentions.forEach((mention, index) => {
            const mentionString = `@SKU:${mention.sku}-(${mention.productName})`;

            entityRanges.push({
                offset: runningOffset,
                length: mentionString.length,
                key: index,
            });

            entityMap[String(index)] = {
                type: "mention",
                mutability: "IMMUTABLE",
                data: { mention },
            };

            runningOffset += mentionString.length + 1; // include " " spacing
        });

        if (mentions.length > 0) {
            runningOffset -= 1; // remove final " " offset
        }

        return {
            blocks: [
                {
                    key: "abcde",
                    text: fullText,
                    type: "unstyled",
                    depth: 0,
                    inlineStyleRanges: [],
                    entityRanges,
                    data: {},
                },
            ],
            entityMap,
        };
    };

    useEffect(() => {
        if (selectedProducts?.length > 0) {
            const rawContentObject = rawContent(selectedProducts, message);
            const contentState = convertFromRaw(rawContentObject);
            setEditorState(EditorState.createWithContent(contentState));
        } else {
            setEditorState(EditorState.createWithContent(ContentState.createFromText(message)));
        }
    }, [message, threadId, selectedProducts]);

    // --- THE FIX IS HERE: Use handleReturn ---
    const handleReturn = useCallback(
        (e, editorState) => {
            if (open) {
                return "not-handled";
            }

            if (e.shiftKey) {
                return "not-handled"; // or undefined
            }

            const currentContent = editorState.getCurrentContent();
            const plainText = currentContent.getPlainText();

            sendMessage(plainText);
            const rawState = convertToRaw(editorState.getCurrentContent());
            const mentions = getMentions(rawState);
            const uniqueMentions = Array.from(new Set(mentions));
            const newIds = uniqueMentions.filter(
                (item) => !selectedIds.some((sel) => sel._id === item._id)
            );
            setSelectedIds((prev) => [...prev, ...newIds]);
            setEditorState(EditorState.createEmpty());
            setTimeout(() => {
                ref.current?.focus();
            }, 0);
            updateSearchParams({
                updated: "true",
            });

            return "handled"; // Tell Draft.js we've handled the "Enter" key
        },
        [open, sendMessage]
    ); // Include dependencies

    // keyBindingFn is just for mapping keys to commands, not for side effects
    const keyBindingFn = useCallback((e) => {
        return getDefaultKeyBinding(e);
    }, []);

    const handleScroll = (e) => {
        // const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        // const isBottom = scrollHeight - scrollTop - clientHeight < 1;
        // if (isBottom && updatedPage < Number(pagination?.noOfPages)) {
        //     const prev = scrollHeight;
        //     setUpdatedPage(updatedPage + 1);
        //     // after fetch:
        //     const newHeight = productRef?.current?.scrollHeight;
        //     productRef.current.scrollTop = newHeight - prev;
        // }
    };

    return (
        <div className="" id="textAreaInput">
            <div
                // className={editorStyles.editor}
                className=" px-4 pt-4 border-b border-slate-200 my-custom-editor-wrapper"
                onClick={() => {
                    ref.current?.focus(); // Focus the editor when the container is clicked
                }}
            >
                <Editor
                    editorState={editorState}
                    onChange={setEditorState}
                    plugins={plugins}
                    ref={ref}
                    placeholder={
                        hasPreviousMessage ? "Reply to Synth" : "Need help? Just type 'Hey Synth'"
                    }
                    placeholderClassName="text-red-400"
                    keyBindingFn={keyBindingFn}
                    handleReturn={handleReturn}
                />
                {/* Mention suggestions dropdown */}

                <div className="">
                    <MentionSuggestions
                        open={open}
                        onOpenChange={onOpenChange}
                        suggestions={suggestions}
                        onSearchChange={onSearchChange}
                        entryComponent={Mention}
                        renderEmptyPopup={true}
                        popoverContainer={({ children, ...props }) => (
                            <Popover {...props}>
                                <div
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        ref.current?.focus();
                                        e.preventDefault();
                                    }}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onScroll={(e) => e.stopPropagation()}
                                    className="bg-white rounded-[8px] z-[1300] "
                                    ref={productRef}
                                    id="product-ref"
                                >
                                    {" "}
                                    <div
                                        className={`border border-slate-200 rounded-[8px]  font-rocGroteskMedium shadow-[0px_4px_12px_-2px_rgba(153,153,153,0.03),0px_10px_15px_-4px_rgba(153,153,153,0.1),0px_8px_6px_0px_rgba(153,153,153,0.04)] w-[400px]  `}
                                    >
                                        <div className="flex items-center justify-between gap-2 bg-slate-50 border-b-slate-200 py-1.5 px-1 rounded-t-[8px]">
                                            {tabs?.map((tab, idx) => (
                                                <span
                                                    key={idx}
                                                    className={`px-2 py-1 cursor-pointer text-[13px] !w-fit ${
                                                        activeTab === tab?.value
                                                            ? "bg-white text-g-75 shadow-[0px_3px_12px_-2px_rgba(108,80,6,0.06)] "
                                                            : "text-slate-400"
                                                    }`}
                                                    onClick={(e) => {
                                                        setActiveTab(tab?.value);
                                                        setProductCategory(tab?.value);
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                    }}
                                                >
                                                    {tab?.label}
                                                </span>
                                            ))}
                                        </div>

                                        {/* <div className="px-3 py-[5px]">
                                            <TextInput
                                                name={"search"}
                                                value={search}
                                                type={"text"}
                                                inputPlaceholder={
                                                    activeTab === "Finished Goods"
                                                        ? "Search finished goods"
                                                        : activeTab === "Raw Material"
                                                        ? "Search components"
                                                        : "Search WIP"
                                                }
                                                onChange={handleSearch}
                                                onInput={handleDebouncedChange}
                                                inputClassName={
                                                    "!h-[38px] pl-[0px] !mb-0 !font-rocGroteskMedium !text-slate-400"
                                                }
                                                leftIcon={
                                                    <i className="ri-search-line before:content-['\f0d1] text-slate-200 mt-[2px]"></i>
                                                }
                                                inputContainerClassname={` !rounded-[4px]  !h-[40px] !pl-[20px] !bg-white !border !border-slate-200`}
                                            />
                                        </div> */}
                                        <div className="h-[230px] overflow-y-scroll">
                                            {productListLoading && data.length === 0 ? (
                                                <div className="flex justify-center my-4 h-[150px] items-center">
                                                    <Loader size={4} />
                                                </div>
                                            ) : data?.length > 0 ? (
                                                <div className="overflow-x-scroll">
                                                    <div
                                                        className=" text-slate-600 font-rocGroteskMedium text-[13px] gap-2 flex items-center
                                                     px-2 py-2"
                                                    >
                                                        <div className="flex flex-col">
                                                            <div className="text-sm  text-slate-600 hover:text-black py-3 cursor-pointer">
                                                                {children}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {productListLoading && (
                                                        <div className="flex justify-center my-4">
                                                            <Loader color="gm-25" size={4} />
                                                        </div>
                                                    )}

                                                    {pagination?.currentPage ===
                                                        pagination?.noOfPages && (
                                                        <div className="flex mb-4 justify-center text-slate-500 text-sm font-rocGroteskMedium">
                                                            End of list
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="h-[150px] flex items-center justify-center">
                                                    <p className="text-slate-500 font-rocGroteskMedium text-sm">
                                                        No {activeTab} available
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </Popover>
                        )}
                    />
                </div>
            </div>

            <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-2">
                    <SynthTooltip
                        divId={"mention-tooltip"}
                        position={"top"}
                        icon={
                            <p className="h-6 w-6 rounded-[6px] bg-slate-100 text-slate-400 flex items-center justify-center">
                                @
                            </p>
                        }
                        content="Use @ to see SKUs or products list"
                        styling={{
                            width: "200px",
                            background: "#142837",
                            borderRadius: "6px",
                        }}
                        contentClass="text-xs text-white"
                    />
                    <p className="text-sm text-slate-400">Use @ to see SKUs or products list</p>
                </div>

                <Button
                    btnText={""}
                    onClick={() => {
                        sendMessage(editorState.getCurrentContent().getPlainText());
                        handleAddProduct();
                        setEditorState(
                            EditorState.createWithContent(ContentState.createFromText(""))
                        );
                        updateSearchParams({
                            updated: "true",
                        });
                    }}
                    btnClassname={"!text-gm-75 !w-fit !rounded-full !py-1 !px-2"}
                    icon={<i className="ri-arrow-up-line text-g-75 text-sm font-bold"></i>}
                />
            </div>
        </div>
    );
};

export default CustomEditor;
